import React, {
  useState, useRef, useEffect, useCallback
} from 'react';
import THDStorageUtils, { LOCAL_STORAGE } from '@thd-olt-global/thd-storage-utils';
import {
  LoadingButton,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Button,
  Checkbox,
  SelectionControlLabel,
  FormGroup,
  FormController,
  Alert
} from '@one-thd/sui-atomic-components';
import { Ruler, ConfirmedFilled, ErrorFilled, WarningFilled } from '@one-thd/sui-icons';
import { useFitCompatibility } from './FitCompatibilityProvider';
import { FitBreakdownModal } from './FitBreakdownModal';
import { track } from '../utils';
import DimensionsForm from './subcomponents/DimensionsForm';
import FitResultsCards from './subcomponents/FitResultsCards';
import { RECOMMENDED_CLEARANCE, APPLIANCE_FITS, SIXTY_DAYS_IN_MS } from '../constants';

export const RefrigeratorPIPWidget = () => {

  const contextValues = useFitCompatibility();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showFitStatus, setShowFitStatus] = useState(false);
  const [fitResult, setFitResult] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: '',
    height: '',
    depth: ''
  });
  const [includeClearance, setIncludeClearance] = useState(true);
  const [invalidFields, setInvalidFields] = useState([]);
  const [showGlobalError, setShowGlobalError] = useState(false);
  const [isCalculateDisabled, setIsCalculateDisabled] = useState(false);
  const [autoCalculate, setAutoCalculate] = useState(true);

  const accordionRef = useRef(null);

  const requiredFields = ['width', 'height', 'depth'];
  const getMissingFields = (obj) => requiredFields.filter((field) => !obj[field] && obj[field] !== 0);

  const {
    isLoading = false,
    itemId,
    header = '',
    subtext = '',
    setDrawerOpen,
    setDrawerInfoIcon,
    setDrawerIndex,
    productDimensions,
    productDepthType,
  } = contextValues || {};
  const overHangAllowance = productDepthType === 'Standard Depth' ? 6 : 3;

  const handleAccordionChange = useCallback((event) => {
    setIsExpanded((prev) => !prev);
  }, []);

const handleDimensionChange = (dimension, rawValue) => {
  const isNumber = /^[0-9]*\.?[0-9]*$/;
  if (rawValue !== '' && !isNumber.test(rawValue)) return;

  let value = rawValue === '.' ? '0.' : rawValue;

  const parts = value.split('.');
  let intPart = parts[0] || '';
  let decPart = parts.length > 1 ? parts[1] : undefined;

  intPart = intPart.slice(0, 3);
  if (decPart !== undefined) {
    decPart = decPart.slice(0, 3);
  }

  let newValue = decPart !== undefined ? `${intPart}.${decPart}` : intPart;
  if (value.endsWith('.') && decPart === '') {
    newValue = `${intPart}.`;
  }

  setDimensions((prev) => {
    const next = { ...prev, [dimension]: newValue };
    if (isCalculateDisabled) {
      const missing = getMissingFields(next);
      setInvalidFields(missing);
      setShowGlobalError(missing.length > 0);
      if (missing.length === 0) {
        setIsCalculateDisabled(false);
      }
    }
    return next;
  });
  setAutoCalculate(false);
};

  const handleOpenModal = () => {
    setAutoCalculate(false);
    setIsModalOpen(true);
    if (typeof track === 'function') {
      track('seeBreakdown');
    }
  };

  const handleCloseModal = () => {
    setIsExpanded(true);
    setIsModalOpen(false);
  };

  const handleOpenDrawerTips = () => {
    setDrawerOpen(true);
    setAutoCalculate(false);
  };

  const getOverallFits = (widthFits, heightFits, depthFits, depthOverhang) => {

    if (widthFits && heightFits && depthFits && depthOverhang === 0) return APPLIANCE_FITS.FITS;
    if (widthFits && heightFits) {
      return depthOverhang <= overHangAllowance
        ? APPLIANCE_FITS.FITS_WITH_OVERFLOW : APPLIANCE_FITS.MOSTLY_FITS;
    }
    return APPLIANCE_FITS.DOES_NOT_FIT;
  };

  const calculateFitCompatibility = () => {

    const savedDimensions = THDStorageUtils.get(LOCAL_STORAGE, { key: 'FIT_COMPATIBILITY_DIMENSIONS' });
    const parsedDimensions = savedDimensions.split(',');
    const parsedWidth = parseFloat(parsedDimensions?.[0]);
    const parsedHeight = parseFloat(parsedDimensions?.[1]);
    const parsedDepth = parseFloat(parsedDimensions?.[2]);

    const userWidth = parseFloat(dimensions?.width) || parsedWidth;
    const userHeight = parseFloat(dimensions?.height) || parsedHeight;
    const userDepth = parseFloat(dimensions?.depth) || parsedDepth;

    if (!userWidth || !userHeight || !userDepth) {
      return null;
    }
    if (dimensions?.width !== userWidth || dimensions?.height !== userHeight || dimensions?.depth !== userDepth) {
      setDimensions({
        width: `${userWidth}`,
        height: `${userHeight}`,
        depth: `${userDepth}`
      });
    }

    if (parsedWidth !== userWidth || parsedHeight !== userHeight || parsedDepth !== userDepth) {
      THDStorageUtils.set(LOCAL_STORAGE, { key: 'FIT_COMPATIBILITY_DIMENSIONS',
        value: `${userWidth},${userHeight},${userDepth}`,
        expiration: SIXTY_DAYS_IN_MS });
    }
    const productWidth = productDimensions?.width ? parseFloat(productDimensions.width) : 36;
    const productHeight = productDimensions?.height ? parseFloat(productDimensions.height) : 70;
    const productDepth = productDimensions?.depth ? parseFloat(productDimensions.depth) : 24;

    let calculatedProductWidth = productWidth;
    let calculatedProductHeight = productHeight;
    let calculatedProductDepth = productDepth;

    if (includeClearance) {
      calculatedProductWidth += RECOMMENDED_CLEARANCE.width;
      calculatedProductHeight += RECOMMENDED_CLEARANCE.height;
      calculatedProductDepth += RECOMMENDED_CLEARANCE.depth;
    }

    const widthFits = userWidth >= calculatedProductWidth;
    const heightFits = userHeight >= calculatedProductHeight;
    const depthFits = userDepth >= calculatedProductDepth;

    const depthOverhang = parseFloat(Math.max(0, calculatedProductDepth - userDepth).toFixed(3));

    const widthClearance = userWidth - calculatedProductWidth;
    const heightClearance = userHeight - calculatedProductHeight;

    const overallFits = getOverallFits(widthFits, heightFits, depthFits, depthOverhang);
    const widthIcon = widthFits ? <ConfirmedFilled color="success" size="small" /> : <ErrorFilled color="danger" size="small" />;
    const heightIcon = heightFits ? <ConfirmedFilled color="success" size="small" /> : <ErrorFilled color="danger" size="small" />;
    const depthIcon = depthOverhang <= overHangAllowance ? <ConfirmedFilled color="success" size="small" /> : <WarningFilled color="warning-subtle" size="small" />;

    return {
      fits: overallFits,
      widthFits,
      heightFits,
      depthFits,
      widthIcon,
      heightIcon,
      depthIcon,
      depthOverhang,
      widthClearance,
      heightClearance,
      productDimensions: {
        calculatedWidth: calculatedProductWidth,
        calculatedHeight: calculatedProductHeight,
        calculatedDepth: calculatedProductDepth,
        width: productWidth,
        height: productHeight,
        depth: productDepth
      }
    };
  };

  useEffect(() => {
    if (autoCalculate) {
      const result = calculateFitCompatibility();
      if (result) {
        setFitResult(result);
        setShowFitStatus(true);
        setIsExpanded(false);
      }
    }
  }, [productDimensions, autoCalculate]);

  useEffect(() => {
    setAutoCalculate(true);
  }, [itemId]);

  const handleCalculateFit = () => {
    try {
      const missing = getMissingFields(dimensions);

      if (missing.length) {
        setInvalidFields(missing);
        setShowGlobalError(true);
        setIsCalculateDisabled(true);
        setShowFitStatus(false);
        setFitResult(null);
        setIsExpanded(true);
        return;
      }

      setInvalidFields([]);
      setShowGlobalError(false);
      setIsCalculateDisabled(false);

      const result = calculateFitCompatibility();
      setFitResult(result);
      setShowFitStatus(true);
      if (typeof track === 'function') {
        track('calculateFit');
      }
      setAutoCalculate(false);
    } catch (error) {
      // Error handling without console logging
    }
  };

  if (!setDrawerOpen || !setDrawerIndex) {
    return <div>Error: Missing context functions</div>;
  }

  const headerClassNames = 'sui-leading-tight sui-font-bold sui-truncate sui-text-wrap sui-text-xl';
  const subtextClassNames = 'sui-leading-tight sui-truncate sui-text-wrap sui-text-base';

  const getResultMessage = () => {
    if (!fitResult) return '';

    // eslint-disable-next-line react/prop-types
    const CustomBoldUnderlineText = ({ children }) => (
      <span className="sui-font-bold">{children}</span>
    );

    if (fitResult.fits === APPLIANCE_FITS.DOES_NOT_FIT) {
      return 'Check your measurements or view other options.';
    }

    if (fitResult.fits === APPLIANCE_FITS.FITS_WITH_OVERFLOW) {
      return (
        <>
          {/* eslint-disable-next-line max-len */}
          This fridge sticks out <CustomBoldUnderlineText>{fitResult.depthOverhang} inches past</CustomBoldUnderlineText> your countertop.
          This depth is still considered a well-fitted installation.
        </>
      );
    }
    if (fitResult.fits === APPLIANCE_FITS.MOSTLY_FITS) {
      return (
        <>
          {/* eslint-disable-next-line max-len */}
          This fridge sticks out <CustomBoldUnderlineText>{fitResult.depthOverhang} inches past</CustomBoldUnderlineText> your countertop.
          Check your walkway and refrigerator door clearances.{productDepthType === 'Standard Depth' && ' Consider a counter-depth model for a better fit.'}
        </> 
      );
    }

    return 'See the breakdown for additional measuring and install tips.';
  };

  const getHeaderIcon = (fitStatus) => {
    if (isExpanded) {
      return <Ruler data-testid="fit-icon" size="large" />;
    }
    switch (fitStatus) {
    case APPLIANCE_FITS.FITS:
      return (
        <ConfirmedFilled
          color="success"
          size="large"
        />
      );
    case APPLIANCE_FITS.FITS_WITH_OVERFLOW:
      return (
        <ConfirmedFilled
          color="success"
          size="large"
        />
      );
    case APPLIANCE_FITS.MOSTLY_FITS:
      return (
        <WarningFilled
          color="warning-subtle"
          size="large"
        />
      );
    case APPLIANCE_FITS.DOES_NOT_FIT:
      return (
        <ErrorFilled
          color="danger"
          size="large"
        />
      );
    default:
      return null;
    }
  };
  const getHeaderSecondaryText = (fitStatus) => {
    switch (fitStatus) {
    case APPLIANCE_FITS.FITS:
      return 'Hooray! This Refrigerator Fits Your Space';
    case APPLIANCE_FITS.FITS_WITH_OVERFLOW:
      return 'Hooray! This Refrigerator Fits Your Space';
    case APPLIANCE_FITS.MOSTLY_FITS:
      return 'This Refrigerator Mostly Fits Your Space';
    case APPLIANCE_FITS.DOES_NOT_FIT:
      return 'This Fridge Doesn\'t Fit Your Space';
    default:
      return '';
    }
  };  

  return (
    <div className={isExpanded ? 'sui-p-4' : 'sui-px-4 sui-pt-4'}>
      <Accordion
        ref={accordionRef}
        expanded={isExpanded}
        noBorders
        onChange={handleAccordionChange}
      >
        <AccordionHeader variant="h4">
          <div className="sui-w-full sui-p-0">
            {showFitStatus && fitResult ? (
              <div className="sui-grid sui-auto-cols-3 sui-gap-x-2 sui-justify-start">
                <span className="sui-mt-0">
                  {getHeaderIcon(fitResult.fits)}
                </span>
                <div className="sui-col-start-2 sui-truncate sui-flex sui-flex-col sui-gap-y-1 sui-mb-2">
                  <h4 className={headerClassNames}>
                    {getHeaderSecondaryText(fitResult.fits)}
                  </h4>
                  <div className={subtextClassNames}>{getResultMessage()}</div>
                </div>
              </div>
            ) : (
              <div className="sui-grid sui-auto-cols-3 sui-gap-x-2 sui-gap-y-1 sui-justify-start sui-mb-4">
                <span className="sui-mt-0">
                  <Ruler data-testid="fit-icon" size="large" />
                </span>
                <div className="sui-col-start-2 sui-truncate sui-flex sui-flex-col sui-gap-y-1">
                  <h4 className={headerClassNames}>{header}</h4>
                  <div className={subtextClassNames}>{subtext}</div>
                </div>
              </div>
            )}
          </div>
        </AccordionHeader>
        <AccordionBody>
          {!showFitStatus || !fitResult ? (
            <div className="sui-w-full">
              <FormGroup orientation="vertical" className="sui-w-full">
                <DimensionsForm
                  dimensions={dimensions}
                  handleDimensionChange={handleDimensionChange}
                  setDrawerIndex={setDrawerIndex}
                  setDrawerInfoIcon={setDrawerInfoIcon}
                  onChangeInfo={() => setAutoCalculate(false)}
                  invalidFields={invalidFields}
                  showGlobalError={showGlobalError}
                />
                <FormController fullWidth>
                  <SelectionControlLabel
                    alignment="top"
                    label="Include recommended clearances in calculation."
                  >
                    <Checkbox
                      defaultChecked
                      checked={includeClearance}
                      onChange={(event) => setIncludeClearance(event.target.checked)}
                    />
                  </SelectionControlLabel>
                </FormController>
              </FormGroup>
              {!includeClearance && (
                <div className="sui-py-3">
                  <Alert status="warning">
                    Removing clearances may cause a tight or improper fit. 
                    The manufacturer recommends allowing space for ventilation and door swing.
                  </Alert>
                </div>
              )}
            </div>
          ) : (
            <div >
              <FitResultsCards
                fitResult={fitResult}
                dimensions={dimensions}
                onEdit={() => {
                  setShowFitStatus(false);
                  setFitResult(null);
                  setIsExpanded(true);
                }}
              />
            </div>
          )}
        </AccordionBody>
      </Accordion>
      {isExpanded && (
        <div className="sui-mt-6">
          {!fitResult && (
            <div
              className="
              sui-w-full
              sui-flex
              sui-gap-4
              sui-grid-cols-2
              sm:sui-grid-cols-3
              md:sui-grid-cols-3
              lg:sui-grid-cols-2
              xl:sui-grid-cols-3
              2xl:sui-grid-cols-3"
            >
              <LoadingButton
                onClick={handleOpenDrawerTips}
                variant="tertiary"
                size="small"
                fullWidth
                data-testid="fit-compatibility-measure-install-tips"
              >
                <span className="sui-whitespace-nowrap">
                  Measure &amp; Install Tips
                </span>
              </LoadingButton>
              <div className="sui-w-3/5 sui-min-w-0">
                <Button
                  loading={isLoading}
                  onClick={handleCalculateFit}
                  variant="secondary"
                  fullWidth
                  size="small"
                  data-testid="fit-compatibility-calculate-fit"
                >
                  <span className="sui-whitespace-nowrap">Calculate Fit</span>
                </Button>
              </div>
            </div>
          )}
          {showFitStatus && fitResult && (
            <div className="sui-w-full xs:sui-w-2/5">
              <LoadingButton
                onClick={handleOpenModal}
                variant="tertiary"
                data-testid="fit-compatibility-see-breakdown"
                size="small"
              >
                See Breakdown
              </LoadingButton>
            </div>
          )}
          {isModalOpen && (
            <FitBreakdownModal
              open={isModalOpen}
              onClose={handleCloseModal}
              fitResult={fitResult}
              userDimensions={dimensions}
              productData={{}}
              setDrawerOpen={setDrawerOpen}
              secondaryHeader={getHeaderSecondaryText(fitResult.fits)}
              resultMessage={getResultMessage()}
              includeClearance={includeClearance}
              overhangTolerance={overHangAllowance}
            />
          )}
        </div>
      )}
    </div>
  );
};